import { computed, unref, watch } from 'vue';
import { useListConfigStore } from '@/stores/listConfig';
import { useGridPreferencesStore } from '@/stores/gridPreferences';

// Resolve a value that may be a plain value, a ref, or a getter function.
const resolve = value => (typeof value === 'function' ? value() : unref(value));

/**
 * Apply a user's per-grid column override (visibility + order) on top of the
 * organization's list configuration.
 *
 * @param {Array} orgColumns  The org's effective columns (all, ordered), each with
 *                            at least { field, visible, locked, ... }.
 * @param {Array} userColumns The user's override: ordered [{ field, visible }].
 *                            Empty/absent means "follow the organization config".
 * @returns {Array} The org columns reordered and re-flagged per the user's choice,
 *                  with any column the user hasn't seen yet appended in org order.
 */
export function mergeUserColumns(orgColumns, userColumns) {
    if (!userColumns || !userColumns.length) {
        return orgColumns;
    }
    const byField = new Map(orgColumns.map(column => [column.field, column]));
    const seen = new Set();
    const merged = [];

    userColumns.forEach(pref => {
        const column = byField.get(pref.field);
        if (!column) {
            return;
        }
        seen.add(column.field);
        merged.push({ ...column, visible: column.locked ? true : !!pref.visible });
    });

    // Columns added to the org config after the user last saved: keep them, in
    // their org position, with the org default visibility.
    orgColumns.forEach(column => {
        if (!seen.has(column.field)) {
            merged.push(column);
        }
    });

    return merged;
}

/**
 * Org-configurable columns for a full-width list table (Settings ▸ Lists), with
 * an optional per-user override (show/hide + reorder) layered on top.
 *
 * Returns a `tableColumns` computed to bind to `<base-list :table-columns>`. The
 * available set, sortability and labels come from the organization's saved
 * configuration; each user may then hide/show and reorder columns for themselves
 * via the grid's column manager — that override wins over the org defaults.
 * Widths stay in the separate per-user grid-preferences (column_widths).
 *
 * Adding a list to the manager is a catalog entry in App\Services\ListColumnCatalog
 * plus this one call — see the `/list-columns` skill.
 *
 * @param {string|import('vue').Ref|(() => string)} objectType
 *        Key registered in ListColumnCatalog (e.g. 'tasks', 'contacts'). Also the
 *        grid key under which the user's column override is stored. May be reactive
 *        when one page serves several object types (e.g. Contacts vs Customers reuse
 *        the same List.vue) — the config reloads when it changes.
 * @param {Object} options
 * @param {Array|(() => Array)}  options.defaults  Fallback columns until the config loads.
 * @param {Object|(() => Object)} options.widths   Per-field column widths, keyed by field.
 */
export function useListColumns(objectType, { defaults = [], widths = {} } = {}) {
    const listConfigStore = useListConfigStore();
    const gridPreferences = useGridPreferencesStore();

    const resolvedType = computed(() => resolve(objectType));

    // Org columns merged with the user's personal show/hide + order override.
    const effectiveColumns = computed(() => {
        const config = listConfigStore.getConfig(resolvedType.value);
        if (!config || !config.length) {
            return null;
        }
        return mergeUserColumns(config, gridPreferences.getColumnConfig(resolvedType.value));
    });

    const tableColumns = computed(() => {
        const columns = effectiveColumns.value;
        if (!columns) {
            return resolve(defaults);
        }
        const widthMap = resolve(widths);
        return columns
            .filter(column => column.visible)
            .map(column => ({
                field: column.field,
                label: column.label,
                sortable: column.sortable,
                width: widthMap[column.field]
            }));
    });

    /**
     * The org's custom fields that are currently enabled as columns. Their cells cannot
     * use hand-written `#col-<field>` slots (the fields are defined at runtime), so the
     * list renders them through dynamic slots + <list-custom-field-cell>.
     */
    const customColumns = computed(() => {
        const columns = effectiveColumns.value;
        if (!columns) {
            return [];
        }
        return columns.filter(column => column.custom && column.visible);
    });

    /**
     * The set of fields the org has enabled for inline editing (Settings ▸ Lists).
     * Until the config loads we fall back to the `editable` flag on the provided
     * defaults, so a list keeps its built-in inline editors on the first render.
     */
    const editableFields = computed(() => {
        const columns = effectiveColumns.value;
        if (!columns) {
            return new Set(
                resolve(defaults)
                    .filter(column => column.editable)
                    .map(column => column.field)
            );
        }
        return new Set(columns.filter(column => column.visible && column.editable).map(column => column.field));
    });

    /** Whether a given column is currently inline-editable. Use to gate a cell's editor. */
    const isEditable = field => editableFields.value.has(field);

    // Load (and reload when the object type changes) — immediate covers the first render.
    // A page that serves both catalog and non-catalog lists (the accounting documents
    // list) passes an empty type for the latter: nothing to fetch, so don't.
    watch(
        resolvedType,
        type => {
            if (type) {
                listConfigStore.loadConfig(type);
            }
        },
        { immediate: true }
    );

    return { tableColumns, customColumns, isEditable };
}
